import type { ConversationMessage, TrainingProposal } from '@deskworks/shared-types';
import { TRAINING_PROPOSAL_EVENTS } from '@deskworks/shared-types';
import { useQueryClient } from '@tanstack/react-query';
import { Link, useNavigate } from '@tanstack/react-router';
import { Check, Flag, Forward, GraduationCap, Rocket, X, Zap } from 'lucide-react';
import { Markdown } from '@/components/markdown';
import { RelativeTime } from '@/components/relative-time';
import { Button } from '@/components/ui/button';
import { api } from '@/lib/api';
import { formatAbsolute } from '@/lib/format';
import { useApiMutation } from '@/lib/hooks';
import { t, tr } from '@/lib/i18n';
import { qk } from '@/lib/query-keys';
import { cn } from '@/lib/utils';

/** Milestones of the training the chat shows as a notice (docs/features/desk-training.md). */
const NOTICE_EVENTS = ['training_started', 'proposal_decided', 'stage_changed', 'stage_ready'];

export function isTrainingProposal(m: ConversationMessage): boolean {
  const event = (m.metadata as { event?: string } | undefined)?.event;
  return (TRAINING_PROPOSAL_EVENTS as readonly string[]).includes(event ?? '');
}

export function isTrainingNotice(m: ConversationMessage): boolean {
  return m.senderType === 'system' && NOTICE_EVENTS.includes((m.metadata as { event?: string } | undefined)?.event ?? '');
}

/**
 * The Lead proposes the next stage or God mode in the training; a person decides with one click
 * (`POST /desks/:id/training/proposal`). Once decided, the card says who decided how.
 */
export function TrainingProposalCard({ message: m, deskId }: { message: ConversationMessage; deskId: string }) {
  const queryClient = useQueryClient();
  const proposal = m.metadata as unknown as TrainingProposal;
  const decide = useApiMutation((accept: boolean) => api.desks.decideTrainingProposal(deskId, { messageId: m.id, accept }), {
    invalidate: [qk.desk(deskId), qk.deskTraining(deskId), qk.conversation(m.conversationId)],
    errorTitle: t('Couldn’t decide the proposal'),
    onSuccess: () => void queryClient.invalidateQueries({ queryKey: ['desks'] }),
  });
  const god = proposal.event === 'god_mode_proposed';
  const end = proposal.event === 'session_end_proposed';
  const decided = proposal.decided;
  return (
    <div className="flex justify-center">
      <div
        className={cn(
          'w-full max-w-[92%] rounded-xl border-2 bg-card px-4 py-3 shadow-xs',
          decided
            ? 'border-border'
            : god
              ? 'border-amber-300 dark:border-amber-800'
              : end
                ? 'border-sky-300 dark:border-sky-800'
                : 'border-emerald-300 dark:border-emerald-800',
        )}
      >
        <div className="flex items-center gap-2 text-xs font-medium text-muted-foreground">
          {god ? <Zap className="size-3.5" /> : end ? <Flag className="size-3.5" /> : <Rocket className="size-3.5" />}
          <span>{god ? t('The Lead proposes God mode') : end ? t('Session done? Are you happy with it?') : t('The Lead proposes the next stage')}</span>
          <RelativeTime value={m.createdAt} className="ml-auto font-normal" />
        </div>
        <Markdown text={proposal.reason || m.content} className="mt-1.5 text-sm" />
        {decided ? (
          <p className={cn('mt-2 flex items-center gap-1.5 text-xs', decided.accepted ? 'text-emerald-700 dark:text-emerald-300' : 'text-muted-foreground')}>
            {decided.accepted ? <Check className="size-3.5" /> : <X className="size-3.5" />}
            {tr('{by} chose “{choice}” {time}.', {
              by: decided.by,
              choice: decided.accepted ? proposal.acceptLabel : proposal.declineLabel,
              time: <RelativeTime value={decided.at} />,
            })}
          </p>
        ) : (
          <div className="mt-3 flex flex-wrap gap-2">
            <Button size="sm" onClick={() => decide.mutate(true)} loading={decide.isPending && decide.variables === true} disabled={decide.isPending}>
              <Check /> {proposal.acceptLabel}
            </Button>
            <Button size="sm" variant="outline" onClick={() => decide.mutate(false)} loading={decide.isPending && decide.variables === false} disabled={decide.isPending}>
              {proposal.declineLabel}
            </Button>
          </div>
        )}
      </div>
    </div>
  );
}

/** A milestone of the training: it started, the desk moved on, a proposal was decided, Deskworks measured it ready. */
export function TrainingNotice({ message: m }: { message: ConversationMessage }) {
  return (
    <div className="flex justify-center">
      <div
        title={formatAbsolute(m.createdAt, true)}
        className="flex max-w-[90%] items-center gap-1.5 rounded-lg bg-sky-50 px-3 py-1.5 text-center text-xs text-sky-800 dark:bg-sky-950/50 dark:text-sky-200"
      >
        <GraduationCap className="size-3.5 shrink-0" />
        <span className="break-words whitespace-pre-wrap">{m.content}</span>
      </div>
    </div>
  );
}

/** A training case the Lead handed over to another desk (`handoff`, docs/features/desk-training.md). */
interface Handoff {
  event: 'handoff';
  deskId: string;
  deskName: string;
  workItemId: string;
  title: string;
  fromWorkItemId: string | null;
}

export function isHandoff(m: ConversationMessage): boolean {
  const meta = m.metadata as Partial<Handoff> | undefined;
  return m.senderType === 'system' && meta?.event === 'handoff' && !!meta.deskId && !!meta.workItemId;
}

/**
 * The case went to another desk: its title links to the new Work Item there, and "Go on in its training" takes it into
 * that desk's training and opens it — so the person keeps working it through, now with that desk's Lead.
 */
export function HandoffCard({ message: m, workspaceId }: { message: ConversationMessage; workspaceId: string }) {
  const navigate = useNavigate();
  const h = m.metadata as unknown as Handoff;
  const goOn = useApiMutation(() => api.desks.startTraining(h.deskId, { workItemId: h.workItemId }), {
    invalidate: [qk.desk(h.deskId), qk.deskTraining(h.deskId), qk.workItem(h.workItemId)],
    errorTitle: t('Couldn’t go on in {desk}’s training', { desk: h.deskName }),
    onSuccess: () => void navigate({ to: '/w/$workspaceId/desks/$deskId', params: { workspaceId, deskId: h.deskId }, search: { tab: 'training' } }),
  });
  const item = (
    <Link
      to="/w/$workspaceId/work-items/$workItemId"
      params={{ workspaceId, workItemId: h.workItemId }}
      className="font-medium underline-offset-2 hover:text-primary hover:underline"
    >
      {h.title}
    </Link>
  );
  return (
    <div className="flex justify-center">
      <div className="w-full max-w-[92%] rounded-xl border-2 border-indigo-200 bg-card px-4 py-3 shadow-xs max-md:max-w-full dark:border-indigo-900">
        <div className="flex items-center gap-2 text-xs font-medium text-muted-foreground">
          <Forward className="size-3.5" />
          <span className="min-w-0 truncate">{t('Handed over to {desk}', { desk: h.deskName })}</span>
          <RelativeTime value={m.createdAt} className="ml-auto shrink-0 font-normal" />
        </div>
        <p className="mt-1.5 text-sm break-words">{item}</p>
        {m.content && <p className="mt-1 text-xs text-muted-foreground">{m.content}</p>}
        <Button className="mt-3 max-md:h-11 max-md:w-full" size="sm" onClick={() => goOn.mutate()} loading={goOn.isPending}>
          <GraduationCap /> {t('Go on in {desk}’s training', { desk: h.deskName })}
        </Button>
        {goOn.isError && (
          <Link
            to="/w/$workspaceId/work-items/$workItemId"
            params={{ workspaceId, workItemId: h.workItemId }}
            className="mt-2 block text-xs font-medium text-muted-foreground underline underline-offset-2 hover:text-foreground max-md:py-2"
          >
            {t('Open the Work Item')}
          </Link>
        )}
      </div>
    </div>
  );
}
